import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  LinearProgress
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  AutoAwesome as EnrichIcon,
  Sync as SyncIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  AccountBox as AccountBoxIcon,
  Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  SelectAll as SelectAllIcon,
  Deselect as DeselectIcon,
  Visibility as VisibilityIcon,
  Campaign as CampaignIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import api from '../api';
import logger from '../utils/logger';
import SalesOutreachShell from './SalesOutreachShell';

const SalesforceRecordsTable = ({ recordType: initialRecordType = 'Lead', onRecordTypeChange }) => {
  const [recordType, setRecordType] = useState(initialRecordType);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // Search, filter, and sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [noPhoneFilter, setNoPhoneFilter] = useState(false);
  const [noEmailFilter, setNoEmailFilter] = useState(false);
  const [sortField, setSortField] = useState('LastModifiedDate');
  const [sortOrder, setSortOrder] = useState('DESC');
  
  // Enrichment state
  const [enriching, setEnriching] = useState(false);
  const [enrichmentResults, setEnrichmentResults] = useState({});
  const [enrichDialogOpen, setEnrichDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [lushaResults, setLushaResults] = useState([]);
  
  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncFields, setSyncFields] = useState({});
  
  // Details modal state
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedRecordDetails, setSelectedRecordDetails] = useState(null);
  
  // Bulk enrichment state
  const [selectedRecords, setSelectedRecords] = useState(new Set());
  const [bulkEnriching, setBulkEnriching] = useState(false);
  const [bulkPreviewOpen, setBulkPreviewOpen] = useState(false);
  const [bulkPreviewData, setBulkPreviewData] = useState([]);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkConfirmAgreed, setBulkConfirmAgreed] = useState(false);
  const [bulkEnrichProgress, setBulkEnrichProgress] = useState(0);
  const [bulkEnrichCurrent, setBulkEnrichCurrent] = useState(0);
  const [bulkEnrichTotal, setBulkEnrichTotal] = useState(0);
  const [bulkEnrichCreditsUsed, setBulkEnrichCreditsUsed] = useState(0);
  
  // SalesOutreach state
  const [salesOutreachOpen, setSalesOutreachOpen] = useState(false);
  const [selectedRecordForOutreach, setSelectedRecordForOutreach] = useState(null);
  
  useEffect(() => {
    fetchRecords();
  }, [recordType, page, rowsPerPage, searchQuery, statusFilter, noPhoneFilter, noEmailFilter, sortField, sortOrder]);
  
  // Clear selections when record type changes
  useEffect(() => {
    setSelectedRecords(new Set());
  }, [recordType]);
  
  const fetchRecords = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const filters = {};
      if (statusFilter) {
        filters.status = statusFilter;
      }
      if (noPhoneFilter) {
        filters.no_phone = 'true';
      }
      if (noEmailFilter) {
        filters.no_email = 'true';
      }
      
      const result = await api.salesforceGetRecords(
        recordType,
        rowsPerPage,
        page * rowsPerPage,
        filters,
        searchQuery,
        sortField,
        sortOrder
      );
      
      if (result.success) {
        setRecords(result.records || []);
        const total = result.total_size || 0;
        setTotalRecords(total);
        logger.log(`Fetched ${result.records?.length || 0} records, total: ${total}, page: ${page}, offset: ${page * rowsPerPage}`);
      } else {
        setError(result.error || 'Failed to fetch records');
      }
    } catch (err) {
      logger.error('Error fetching Salesforce records:', err);
      setError(err.response?.data?.error || err.message || 'Failed to fetch records');
    } finally {
      setLoading(false);
    }
  };
  
  const handleRecordTypeChange = (event) => {
    const newType = event.target.value;
    setRecordType(newType);
    setPage(0);
    setStatusFilter(''); // Reset status filter when changing record type
    if (onRecordTypeChange) {
      onRecordTypeChange(newType);
    }
  };
  
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0); // Reset to first page when searching
  };
  
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0); // Reset to first page when filtering
  };
  
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle sort order if clicking same field
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      // Set new sort field, default to ASC
      setSortField(field);
      setSortOrder('ASC');
    }
    setPage(0); // Reset to first page when sorting
  };
  
  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortOrder === 'ASC' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />;
  };
  
  // Get unique status values from current records for filter dropdown
  const getAvailableStatuses = () => {
    const statuses = new Set();
    records.forEach(record => {
      if (record.Status) {
        statuses.add(record.Status);
      }
    });
    return Array.from(statuses).sort();
  };
  
  const handleEnrich = async (record) => {
    try {
      setEnriching(true);
      setError(null);
      setSelectedRecord(record);
      
      const result = await api.salesforceEnrich([record.Id], recordType);
      
      if (result.success && result.results && result.results.length > 0) {
        const enrichmentResult = result.results[0];
        const lushaResults = enrichmentResult.lusha_results || [];
        
        // Check if we have meaningful contact data (not just empty objects)
        const hasMeaningfulData = lushaResults.some(contact => 
          contact.email || 
          contact.phone || 
          (contact.firstName && contact.lastName) ||
          contact.linkedinUrl
        );
        
        if (hasMeaningfulData) {
          setLushaResults(lushaResults);
          setEnrichmentResults({
            ...enrichmentResults,
            [record.Id]: enrichmentResult
          });
          setEnrichDialogOpen(true);
        } else {
          setError(enrichmentResult.error || 'No contact information found in enrichment results');
        }
      } else {
        setError('No enrichment results found');
      }
    } catch (err) {
      logger.error('Error enriching record:', err);
      setError(err.response?.data?.error || err.message || 'Failed to enrich record');
    } finally {
      setEnriching(false);
    }
  };
  
  const handleSync = async () => {
    if (!selectedRecord || Object.keys(syncFields).length === 0) {
      setError('Please select fields to sync');
      return;
    }
    
    try {
      setSyncing(true);
      setError(null);
      
      const result = await api.salesforceSync(recordType, selectedRecord.Id, syncFields);
      
      if (result.success) {
        setSyncDialogOpen(false);
        setSyncFields({});
        await fetchRecords(); // Refresh records
        setError(null);
      } else {
        setError(result.error || 'Failed to sync to Salesforce');
      }
    } catch (err) {
      logger.error('Error syncing to Salesforce:', err);
      setError(err.response?.data?.error || err.message || 'Failed to sync');
    } finally {
      setSyncing(false);
    }
  };
  
  const getRecordTypeIcon = () => {
    switch (recordType) {
      case 'Lead':
        return <PersonIcon />;
      case 'Contact':
        return <PersonIcon />;
      case 'Account':
        return <BusinessIcon />;
      default:
        return <AccountBoxIcon />;
    }
  };
  
  const getRecordDisplayName = (record) => {
    if (recordType === 'Account') {
      return record.Name || 'N/A';
    }
    const firstName = record.FirstName || '';
    const lastName = record.LastName || '';
    return `${firstName} ${lastName}`.trim() || 'N/A';
  };
  
  const getRecordCompany = (record) => {
    if (recordType === 'Account') {
      return record.Name || 'N/A';
    }
    if (recordType === 'Contact') {
      return record.Account?.Name || 'N/A';
    }
    return record.Company || 'N/A';
  };
  
  const getRecordWebsite = (record) => {
    if (recordType === 'Lead') {
      return record.Website || 'N/A';
    }
    if (recordType === 'Contact') {
      return record.Account?.Website || 'N/A';
    }
    return record.Website || 'N/A';
  };
  
  const getRecordCountry = (record) => {
    if (recordType === 'Lead') {
      return record.Country || 'N/A';
    }
    if (recordType === 'Contact') {
      return record.MailingCountry || 'N/A';
    }
    return record.BillingCountry || 'N/A';
  };
  
  // Extract domain from website or email
  const extractDomain = (record) => {
    // Try website first
    let url = getRecordWebsite(record);
    if (url && url !== 'N/A') {
      try {
        // Remove protocol, www, and path
        let domain = url.replace(/^https?:\/\//, '').replace(/^www\./, '');
        domain = domain.split('/')[0].split('?')[0];
        domain = domain.replace(/\.$/, '');
        
        // Make sure it's a valid domain (contains at least one dot)
        if (domain.includes('.')) {
          return domain.toLowerCase();
        }
      } catch (e) {
        logger.error('Error extracting domain from website:', e);
      }
    }
    
    // Fallback to email domain
    if (recordType !== 'Account' && record.Email) {
      const email = record.Email;
      if (email.includes('@')) {
        const domain = email.split('@')[1];
        if (domain && domain.includes('.')) {
          return domain.toLowerCase();
        }
      }
    }
    
    // For Contacts, try Account website
    if (recordType === 'Contact' && record.Account?.Website) {
      try {
        let domain = record.Account.Website.replace(/^https?:\/\//, '').replace(/^www\./, '');
        domain = domain.split('/')[0].split('?')[0];
        domain = domain.replace(/\.$/, '');
        
        if (domain.includes('.')) {
          return domain.toLowerCase();
        }
      } catch (e) {
        logger.error('Error extracting domain from Account website:', e);
      }
    }
    
    return null;
  };
  
  const formatUrl = (url) => {
    if (!url) return url;
    url = url.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    try {
      const urlObj = new URL(url);
      return urlObj.href;
    } catch (e) {
      logger.error('Invalid URL format:', url);
      return url;
    }
  };

  const extractDomainFromUrl = (url) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.toLowerCase();
    } catch (e) {
      logger.error('Failed to extract domain from URL:', url);
      return url.replace(/^https?:\/\//, '').split('/')[0].toLowerCase();
    }
  };

  const extractCompanyNameFromUrl = (url) => {
    try {
      const domain = extractDomainFromUrl(url);
      const parts = domain.split('.');
      if (parts.length >= 2) {
        return parts[parts.length - 2].charAt(0).toUpperCase() + parts[parts.length - 2].slice(1);
      }
      return domain;
    } catch (e) {
      logger.error('Failed to extract company name from URL:', url);
      return 'Unknown Company';
    }
  };

  const handleOpenSalesOutreach = async (record) => {
    const domain = extractDomain(record);
    if (!domain) {
      setError('No website or email domain found for this record');
      return;
    }
    
    // Get website URL or construct from domain
    let websiteUrl = getRecordWebsite(record);
    if (!websiteUrl || websiteUrl === 'N/A') {
      websiteUrl = `https://${domain}`;
    }
    
    // Format URL properly
    const cleanUrl = formatUrl(websiteUrl);
    const companyName = getRecordCompany(record) !== 'N/A' ? getRecordCompany(record) : extractCompanyNameFromUrl(cleanUrl);
    
    try {
      // Check if a database record exists for this URL (like domain search does)
      logger.log(`Checking if URL exists in records: ${cleanUrl} (Domain: ${domain})`);
      const urlCheckResult = await api.checkUrlInRecords(cleanUrl);
      
      let dbRecord;
      
      if (urlCheckResult.success && urlCheckResult.found && urlCheckResult.records.length > 0) {
        logger.log(`Found ${urlCheckResult.count} existing records for URL: ${cleanUrl}`);
        dbRecord = urlCheckResult.records[0];
      } else {
        logger.log(`No existing records found for URL: ${cleanUrl}, creating a new record`);
        
        // Create a database record like domain search does
        dbRecord = {
          Domain: domain,
          Instellingsnaam: companyName,
          Telefoonnummer: '-',
          URL: cleanUrl,
          'Vestigingsadres Plaats': '-',
          Website: cleanUrl,
          record_id: crypto.randomUUID()
        };
        
        try {
          const addResult = await api.addRecord(dbRecord);
          if (addResult.success) {
            dbRecord.record_id = addResult.record_id;
            logger.log(`Created new record with ID: ${dbRecord.record_id}`);
          }
        } catch (addErr) {
          logger.error('Error adding record:', addErr);
        }
      }
      
      // Create contactInfo object with the database record data
      // Only include relevant fields from Salesforce record (Email, etc.)
      const contactInfo = {
        ...dbRecord,
        Website: cleanUrl,
        Email: record.Email || null,
        URL: cleanUrl,
        Domain: domain
      };
      
      setSelectedRecordForOutreach({
        contactInfo,
        dbRecord
      });
      setSalesOutreachOpen(true);
    } catch (err) {
      logger.error('Error preparing SalesOutreach:', err);
      setError('Failed to prepare outreach data. Please try again.');
    }
  };
  
  const handleCloseSalesOutreach = () => {
    setSalesOutreachOpen(false);
    setSelectedRecordForOutreach(null);
  };
  
  const handleRowClick = (record) => {
    setSelectedRecordDetails(record);
    setDetailsDialogOpen(true);
  };
  
  const formatFieldValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  };
  
  const formatFieldLabel = (fieldName) => {
    // Convert field names to readable labels
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };
  
  // Bulk enrichment handlers
  const handleSelectAll = () => {
    if (selectedRecords.size === records.length) {
      setSelectedRecords(new Set());
    } else {
      setSelectedRecords(new Set(records.map(r => r.Id)));
    }
  };
  
  const handleSelectRecord = (recordId) => {
    const newSelected = new Set(selectedRecords);
    if (newSelected.has(recordId)) {
      newSelected.delete(recordId);
    } else {
      newSelected.add(recordId);
    }
    setSelectedRecords(newSelected);
  };
  
  const handleBulkEnrichClick = () => {
    if (selectedRecords.size === 0) {
      setError('Please select at least one record to enrich');
      return;
    }
    setBulkConfirmAgreed(false);
    setBulkConfirmOpen(true);
  };
  
  const handleBulkEnrichConfirm = async () => {
    if (!bulkConfirmAgreed) {
      setError('Please acknowledge the credit usage warning before proceeding');
      return;
    }
    
    setBulkConfirmOpen(false);
    await performBulkEnrich();
  };
  
  const performBulkEnrich = async () => {
    try {
      setBulkEnriching(true);
      setBulkEnrichProgress(0);
      setBulkEnrichCurrent(0);
      setBulkEnrichCreditsUsed(0);
      setError(null);
      
      const recordIds = Array.from(selectedRecords);
      const totalRecords = recordIds.length;
      setBulkEnrichTotal(totalRecords);
      
      // Process records one by one for real-time progress feedback
      const enrichmentResults = [];
      let totalCreditsUsed = 0;
      
      for (let i = 0; i < recordIds.length; i++) {
        const recordId = recordIds[i];
        const currentNumber = i + 1;
        
        try {
          // Enrich one record at a time
          const result = await api.salesforceEnrich([recordId], recordType);
          
          if (result.success && result.results && result.results.length > 0) {
            const enrichmentResult = result.results[0];
            enrichmentResults.push(enrichmentResult);
            
            // Track credits used - check both credits_used and credits_charged
            // If credits_charged is true but credits_used is not provided, assume 1 credit
            let creditsUsed = enrichmentResult.credits_used || 0;
            if (creditsUsed === 0 && enrichmentResult.credits_charged) {
              creditsUsed = 1;
            }
            totalCreditsUsed += creditsUsed;
            setBulkEnrichCreditsUsed(totalCreditsUsed);
          } else {
            enrichmentResults.push({
              salesforce_record_id: recordId,
              enrichment_success: false,
              error: result.error || 'Enrichment failed',
              lusha_results: []
            });
          }
          
          // Update progress after each record
          const progressPercent = (currentNumber / totalRecords) * 100;
          setBulkEnrichCurrent(currentNumber);
          setBulkEnrichProgress(progressPercent);
        } catch (err) {
          logger.error(`Error enriching record ${recordId}:`, err);
          enrichmentResults.push({
            salesforce_record_id: recordId,
            enrichment_success: false,
            error: err.response?.data?.error || err.message || 'Enrichment error',
            lusha_results: []
          });
          
          // Still update progress even on error
          const progressPercent = (currentNumber / totalRecords) * 100;
          setBulkEnrichCurrent(currentNumber);
          setBulkEnrichProgress(progressPercent);
        }
      }
      
      // Process results to create preview data
      const result = {
        success: true,
        results: enrichmentResults
      };
      
      if (result.success && result.results) {
        // Process results to create preview data
        const previewData = result.results.map((enrichmentResult) => {
          const record = records.find(r => r.Id === enrichmentResult.salesforce_record_id);
          const lushaContact = enrichmentResult.lusha_results?.[0];
          
          if (!lushaContact || !enrichmentResult.enrichment_success) {
            return {
              recordId: enrichmentResult.salesforce_record_id,
              recordName: record ? getRecordDisplayName(record) : 'Unknown',
              error: enrichmentResult.error || 'No data found',
              hasData: false,
              ignored: false // Track if user wants to ignore this record
            };
          }
          
          // Compare existing vs new data
          const changes = {
            added: [],
            changed: []
          };
          
          // Email comparison
          if (lushaContact.email) {
            const existingEmail = record?.Email || '';
            if (!existingEmail) {
              changes.added.push({ field: 'Email', value: lushaContact.email });
            } else if (existingEmail.toLowerCase() !== lushaContact.email.toLowerCase()) {
              changes.changed.push({ 
                field: 'Email', 
                oldValue: existingEmail, 
                newValue: lushaContact.email 
              });
            }
          }
          
          // Phone comparison
          if (lushaContact.phone) {
            const existingPhone = record?.Phone || '';
            if (!existingPhone) {
              changes.added.push({ field: 'Phone', value: lushaContact.phone });
            } else if (existingPhone !== lushaContact.phone) {
              changes.changed.push({ 
                field: 'Phone', 
                oldValue: existingPhone, 
                newValue: lushaContact.phone 
              });
            }
          }
          
          // Title comparison
          if (lushaContact.jobTitle) {
            const existingTitle = record?.Title || '';
            if (!existingTitle) {
              changes.added.push({ field: 'Title', value: lushaContact.jobTitle });
            } else if (existingTitle !== lushaContact.jobTitle) {
              changes.changed.push({ 
                field: 'Title', 
                oldValue: existingTitle, 
                newValue: lushaContact.jobTitle 
              });
            }
          }
          
          return {
            recordId: enrichmentResult.salesforce_record_id,
            recordName: record ? getRecordDisplayName(record) : 'Unknown',
            record: record,
            lushaContact: lushaContact,
            changes: changes,
            hasData: true,
            enrichmentResult: enrichmentResult,
            ignored: false // Track if user wants to ignore this record
          };
        });
        
        setBulkPreviewData(previewData);
        setBulkPreviewOpen(true);
      } else {
        setError(result.error || 'Failed to enrich records');
      }
    } catch (err) {
      logger.error('Error bulk enriching records:', err);
      setError(err.response?.data?.error || err.message || 'Failed to enrich records');
    } finally {
      setBulkEnriching(false);
      setBulkEnrichProgress(0);
      setBulkEnrichCurrent(0);
      setBulkEnrichTotal(0);
      setBulkEnrichCreditsUsed(0);
    }
  };
  
  const handleBulkSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      
      // Prepare bulk update data
      const recordsToUpdate = bulkPreviewData
        .filter(item => !item.ignored && item.hasData && (item.changes.added.length > 0 || item.changes.changed.length > 0))
        .map(item => {
          const updateFields = {};
          
          // Add new fields
          item.changes.added.forEach(change => {
            updateFields[change.field] = change.value;
          });
          
          // Update changed fields
          item.changes.changed.forEach(change => {
            updateFields[change.field] = change.newValue;
          });
          
          return {
            record_id: item.recordId,
            update_fields: updateFields
          };
        });
      
      if (recordsToUpdate.length === 0) {
        setError('No records to update');
        return;
      }
      
      const result = await api.salesforceBulkSync(recordType, recordsToUpdate);
      
      if (result.success) {
        setBulkPreviewOpen(false);
        setBulkPreviewData([]);
        setSelectedRecords(new Set());
        await fetchRecords(); // Refresh records
        setError(null);
      } else {
        setError(result.error || 'Failed to sync to Salesforce');
      }
    } catch (err) {
      logger.error('Error bulk syncing to Salesforce:', err);
      setError(err.response?.data?.error || err.message || 'Failed to sync');
    } finally {
      setSyncing(false);
    }
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
        {/* Top row: Record type, count, refresh */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Record Type</InputLabel>
              <Select
                value={recordType}
                label="Record Type"
                onChange={handleRecordTypeChange}
              >
                <MenuItem value="Lead">Leads</MenuItem>
                <MenuItem value="Contact">Contacts</MenuItem>
                <MenuItem value="Account">Accounts</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              {totalRecords} {recordType.toLowerCase()}s found
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {selectedRecords.size > 0 && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<EnrichIcon />}
                onClick={handleBulkEnrichClick}
                disabled={bulkEnriching || loading}
                size="small"
              >
                Bulk Enrich ({selectedRecords.size})
              </Button>
            )}
            <Tooltip title="Refresh">
              <IconButton onClick={fetchRecords} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        {/* Search and filters row */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by name, email, company..."
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            sx={{ minWidth: 300, flexGrow: 1 }}
          />
          {recordType === 'Lead' && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {getAvailableStatuses().map(status => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {recordType !== 'Account' && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={noEmailFilter}
                    onChange={(e) => {
                      setNoEmailFilter(e.target.checked);
                      setPage(0);
                    }}
                    size="small"
                  />
                }
                label="No Email"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={noPhoneFilter}
                    onChange={(e) => {
                      setNoPhoneFilter(e.target.checked);
                      setPage(0);
                    }}
                    size="small"
                  />
                }
                label="No Phone"
              />
            </>
          )}
        </Box>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedRecords.size > 0 && selectedRecords.size < records.length}
                  checked={records.length > 0 && selectedRecords.size === records.length}
                  onChange={handleSelectAll}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort(recordType === 'Account' ? 'Name' : 'FirstName')}>
                  Name
                  {getSortIcon(recordType === 'Account' ? 'Name' : 'FirstName')}
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort(recordType === 'Account' ? 'Name' : recordType === 'Lead' ? 'Company' : 'Account.Name')}>
                  Company
                  {getSortIcon(recordType === 'Account' ? 'Name' : recordType === 'Lead' ? 'Company' : 'Account.Name')}
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('Title')}>
                  Job Title
                  {getSortIcon('Title')}
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('Website')}>
                  Website
                  {getSortIcon('Website')}
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort(recordType === 'Lead' ? 'Country' : recordType === 'Contact' ? 'MailingCountry' : 'BillingCountry')}>
                  Country
                  {getSortIcon(recordType === 'Lead' ? 'Country' : recordType === 'Contact' ? 'MailingCountry' : 'BillingCountry')}
                </Box>
              </TableCell>
              {recordType !== 'Account' && (
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('Email')}>
                    Email
                    {getSortIcon('Email')}
                  </Box>
                </TableCell>
              )}
              {recordType !== 'Account' && <TableCell>Phone</TableCell>}
              {recordType === 'Lead' && (
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('Status')}>
                    Status
                    {getSortIcon('Status')}
                  </Box>
                </TableCell>
              )}
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={recordType === 'Account' ? 10 : recordType === 'Lead' ? 11 : 10} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={recordType === 'Account' ? 10 : recordType === 'Lead' ? 11 : 10} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No {recordType.toLowerCase()}s found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              records.map((record) => (
                <TableRow 
                  key={record.Id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleRowClick(record)}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedRecords.has(record.Id)}
                      onChange={() => handleSelectRecord(record.Id)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{getRecordDisplayName(record)}</TableCell>
                  <TableCell>{getRecordCompany(record)}</TableCell>
                  <TableCell>{record.Title || 'N/A'}</TableCell>
                  <TableCell>
                    {getRecordWebsite(record) !== 'N/A' ? (
                      <a 
                        href={getRecordWebsite(record)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: 'inherit', textDecoration: 'none' }}
                      >
                        {getRecordWebsite(record)}
                      </a>
                    ) : (
                      'N/A'
                    )}
                  </TableCell>
                  <TableCell>{getRecordCountry(record)}</TableCell>
                  {recordType !== 'Account' && (
                    <>
                      <TableCell>{record.Email || 'N/A'}</TableCell>
                      <TableCell>{record.Phone || 'N/A'}</TableCell>
                    </>
                  )}
                  {recordType === 'Lead' && (
                    <TableCell>
                      {record.Status && (
                        <Chip label={record.Status} size="small" />
                      )}
                    </TableCell>
                  )}
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      <Tooltip title="Sales Outreach">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenSalesOutreach(record)}
                          disabled={!extractDomain(record)}
                          color="default"
                        >
                          <CampaignIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Enrich Contact">
                        <IconButton
                          size="small"
                          onClick={() => handleEnrich(record)}
                          disabled={enriching}
                        >
                          <EnrichIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalRecords}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>
      
      {/* Enrichment Dialog */}
      <Dialog
        open={enrichDialogOpen}
        onClose={() => setEnrichDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Enrichment Results - {selectedRecord && getRecordDisplayName(selectedRecord)}
        </DialogTitle>
        <DialogContent>
          {lushaResults.length > 0 ? (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Found {lushaResults.length} contact(s) from Lusha:
              </Typography>
              {lushaResults.map((contact, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="h6">
                        {contact.firstName} {contact.lastName}
                      </Typography>
                      {contact.jobTitle && (
                        <Typography variant="body2" color="text.secondary">
                          {contact.jobTitle}
                        </Typography>
                      )}
                    </Grid>
                    {contact.email && (
                      <Grid item xs={12}>
                        <Typography variant="body2">
                          <strong>Email:</strong> {contact.email}
                        </Typography>
                      </Grid>
                    )}
                    {contact.phone && (
                      <Grid item xs={12}>
                        <Typography variant="body2">
                          <strong>Phone:</strong> {contact.phone}
                        </Typography>
                      </Grid>
                    )}
                    {contact.linkedinUrl && (
                      <Grid item xs={12}>
                        <Typography variant="body2">
                          <strong>LinkedIn:</strong>{' '}
                          <a href={contact.linkedinUrl} target="_blank" rel="noopener noreferrer">
                            {contact.linkedinUrl}
                          </a>
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              ))}
              <Button
                variant="contained"
                startIcon={<SyncIcon />}
                onClick={async () => {
                  // Sync directly without confirmation dialog
                  if (lushaResults.length > 0) {
                    const firstResult = lushaResults[0];
                    const fieldsToSync = {};
                    if (firstResult.email) {
                      fieldsToSync.Email = firstResult.email;
                    }
                    if (firstResult.phone) {
                      fieldsToSync.Phone = firstResult.phone;
                    }
                    
                    if (Object.keys(fieldsToSync).length > 0) {
                      try {
                        setSyncing(true);
                        setError(null);
                        
                        const result = await api.salesforceSync(recordType, selectedRecord.Id, fieldsToSync);
                        
                        if (result.success) {
                          setEnrichDialogOpen(false);
                          await fetchRecords(); // Refresh records
                          setError(null);
                        } else {
                          setError(result.error || 'Failed to sync to Salesforce');
                        }
                      } catch (err) {
                        logger.error('Error syncing to Salesforce:', err);
                        setError(err.response?.data?.error || err.message || 'Failed to sync');
                      } finally {
                        setSyncing(false);
                      }
                    } else {
                      setError('No fields available to sync');
                    }
                  }
                }}
                disabled={syncing}
                sx={{ mt: 2 }}
              >
                {syncing ? 'Syncing...' : 'Sync to Salesforce'}
              </Button>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No enrichment results found for this record.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnrichDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Sync Dialog */}
      <Dialog
        open={syncDialogOpen}
        onClose={() => setSyncDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Sync Enriched Data to Salesforce</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select which fields to update in Salesforce:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Email"
              value={syncFields.Email || ''}
              onChange={(e) => setSyncFields({ ...syncFields, Email: e.target.value })}
              fullWidth
            />
            <TextField
              label="Phone"
              value={syncFields.Phone || ''}
              onChange={(e) => setSyncFields({ ...syncFields, Phone: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSync}
            variant="contained"
            disabled={syncing || Object.keys(syncFields).length === 0}
          >
            {syncing ? <CircularProgress size={24} /> : 'Sync'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Record Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh' }
        }}
      >
        <DialogTitle>
          {selectedRecordDetails && getRecordDisplayName(selectedRecordDetails)} - Details
        </DialogTitle>
        <DialogContent>
          {selectedRecordDetails && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                {Object.keys(selectedRecordDetails)
                  .filter(key => key !== 'attributes' && selectedRecordDetails[key] !== null && selectedRecordDetails[key] !== undefined && selectedRecordDetails[key] !== '')
                  .map((key) => {
                    const value = selectedRecordDetails[key];
                    let displayValue = formatFieldValue(value);
                    
                    // Handle nested objects (like Account.Name)
                    if (typeof value === 'object' && !Array.isArray(value)) {
                      const nestedKeys = Object.keys(value).filter(k => k !== 'attributes');
                      if (nestedKeys.length > 0) {
                        displayValue = nestedKeys.map(nk => `${formatFieldLabel(nk)}: ${formatFieldValue(value[nk])}`).join(', ');
                      }
                    }
                    
                    // Format URLs as links
                    const isUrl = typeof displayValue === 'string' && (
                      displayValue.startsWith('http://') || 
                      displayValue.startsWith('https://') ||
                      displayValue.startsWith('www.')
                    );
                    
                    return (
                      <Grid item xs={12} sm={6} key={key}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          {formatFieldLabel(key)}
                        </Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                          {isUrl ? (
                            <a 
                              href={displayValue.startsWith('www.') ? `https://${displayValue}` : displayValue}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: 'inherit' }}
                            >
                              {displayValue}
                            </a>
                          ) : (
                            displayValue
                          )}
                        </Typography>
                      </Grid>
                    );
                  })}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          {selectedRecordDetails && (
            <Button
              variant="contained"
              startIcon={<EnrichIcon />}
              onClick={() => {
                setDetailsDialogOpen(false);
                handleEnrich(selectedRecordDetails);
              }}
              disabled={enriching}
            >
              Enrich
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Bulk Enrichment Preview Dialog */}
      <Dialog
        open={bulkPreviewOpen}
        onClose={() => setBulkPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box>
            <Typography variant="h6">
              Bulk Enrichment Preview - {bulkPreviewData.length} Record(s)
            </Typography>
            {bulkPreviewData.filter(item => item.ignored).length > 0 && (
              <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                {bulkPreviewData.filter(item => item.ignored).length} record(s) marked to ignore
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Review the changes that will be made to Salesforce records. Fields marked with <strong>+</strong> will be added, fields marked with <strong>→</strong> will be updated. Use the <strong>Ignore</strong> checkbox to exclude records from syncing.
            </Typography>
            
            {bulkPreviewData.map((item, index) => (
              <Paper 
                key={index} 
                sx={{ 
                  p: 2, 
                  mb: 2,
                  opacity: item.ignored ? 0.6 : 1,
                  border: item.ignored ? '2px dashed' : '1px solid',
                  borderColor: item.ignored ? 'warning.main' : 'divider'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    {item.recordName}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={item.ignored || false}
                        onChange={(e) => {
                          const updatedData = [...bulkPreviewData];
                          updatedData[index].ignored = e.target.checked;
                          setBulkPreviewData(updatedData);
                        }}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" color={item.ignored ? 'warning.main' : 'text.secondary'}>
                        {item.ignored ? 'Ignored' : 'Ignore'}
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                </Box>
                {item.ignored && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This record will be skipped during sync.
                  </Alert>
                )}
                
                {!item.hasData ? (
                  <Alert severity="warning">
                    {item.error || 'No enrichment data available'}
                  </Alert>
                ) : (
                  <Box>
                    {item.changes.added.length === 0 && item.changes.changed.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No changes to apply (all fields already match)
                      </Typography>
                    ) : (
                      <Grid container spacing={2}>
                        {item.changes.added.length > 0 && (
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" color="success.main" gutterBottom>
                              Fields to Add:
                            </Typography>
                            {item.changes.added.map((change, idx) => (
                              <Box key={idx} sx={{ mb: 1, pl: 2 }}>
                                <Typography variant="body2">
                                  <strong>+ {change.field}:</strong> {change.value}
                                </Typography>
                              </Box>
                            ))}
                          </Grid>
                        )}
                        
                        {item.changes.changed.length > 0 && (
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" color="warning.main" gutterBottom>
                              Fields to Update:
                            </Typography>
                            {item.changes.changed.map((change, idx) => (
                              <Box key={idx} sx={{ mb: 1, pl: 2 }}>
                                <Typography variant="body2">
                                  <strong>→ {change.field}:</strong>
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                                  Current: {change.oldValue || 'N/A'}
                                </Typography>
                                <Typography variant="body2" color="primary.main" sx={{ pl: 2 }}>
                                  New: {change.newValue}
                                </Typography>
                              </Box>
                            ))}
                          </Grid>
                        )}
                      </Grid>
                    )}
                  </Box>
                )}
              </Paper>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkPreviewOpen(false)} disabled={syncing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SyncIcon />}
            onClick={handleBulkSync}
            disabled={syncing || bulkPreviewData.filter(item => !item.ignored && item.hasData && (item.changes.added.length > 0 || item.changes.changed.length > 0)).length === 0}
          >
            {syncing ? 'Syncing...' : 'Sync to Salesforce'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Bulk Enrichment Confirmation Dialog */}
      <Dialog
        open={bulkConfirmOpen}
        onClose={() => {
          if (!bulkEnriching) {
            setBulkConfirmOpen(false);
            setBulkConfirmAgreed(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon sx={{ color: 'warning.main' }} />
            <Typography variant="h6">Bulk Enrichment Warning</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Credit Usage Warning
            </Typography>
            <Typography variant="body2">
              You are about to enrich <strong>{selectedRecords.size} record(s)</strong>. Bulk enrichment actions can generate high volumes of credit usage and may:
            </Typography>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              <li><Typography variant="body2">Exceed limits of fair use</Typography></li>
              <li><Typography variant="body2">Result in additional costs to your subscription</Typography></li>
              <li><Typography variant="body2">Consume significant credits from your account</Typography></li>
            </Box>
            <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>
              Please ensure you understand the potential costs before proceeding.
            </Typography>
          </Alert>
          <FormControlLabel
            control={
              <Checkbox
                checked={bulkConfirmAgreed}
                onChange={(e) => setBulkConfirmAgreed(e.target.checked)}
                disabled={bulkEnriching}
              />
            }
            label="I understand the potential credit usage and costs, and agree to proceed with bulk enrichment"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setBulkConfirmOpen(false);
              setBulkConfirmAgreed(false);
            }}
            disabled={bulkEnriching}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkEnrichConfirm}
            variant="contained"
            color="warning"
            disabled={bulkEnriching || !bulkConfirmAgreed}
          >
            Proceed with Bulk Enrichment
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Bulk Enrichment Progress Dialog */}
      <Dialog
        open={bulkEnriching}
        onClose={() => {}} // Prevent closing during enrichment
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bulk Enrichment in Progress</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Enriching {bulkEnrichTotal || selectedRecords.size} record(s)... Please wait.
            </Typography>
            <Box sx={{ mt: 3, mb: 2 }}>
              <LinearProgress 
                variant="determinate" 
                value={bulkEnrichProgress} 
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {bulkEnrichCurrent > 0 ? `${bulkEnrichCurrent} / ${bulkEnrichTotal}` : 'Starting...'} ({Math.round(bulkEnrichProgress)}%)
                </Typography>
                <Typography variant="body2" color="primary.main" sx={{ fontWeight: 'bold' }}>
                  Credits Used: {bulkEnrichCreditsUsed}
                </Typography>
              </Box>
            </Box>
            <Alert severity="info">
              <Typography variant="body2">
                This process may take some time depending on the number of records. Do not close this dialog.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
      </Dialog>
      
      {/* Sales Outreach */}
      {selectedRecordForOutreach && selectedRecordForOutreach.dbRecord && (
        <SalesOutreachShell
          open={salesOutreachOpen}
          onClose={handleCloseSalesOutreach}
          recordId={selectedRecordForOutreach.dbRecord.record_id}
          companyName={selectedRecordForOutreach.contactInfo.Instellingsnaam || selectedRecordForOutreach.contactInfo.companyName || 'Unknown Company'}
          contactInfo={selectedRecordForOutreach.contactInfo}
          openWithUrlSearch={true}
        />
      )}
    </Box>
  );
};

export default SalesforceRecordsTable;

